import { Alert, ConfirmModal, Link, Tooltip, showToast } from '@lokalise/louis';
import { useCallback, useState } from 'react';

import { useCloseLanguageMutation } from '@app/api/tasks';
import track, { AnalyticsEventName } from '@app/utils/analytics';

import { Cell, Row, Status, StrongInlineContent } from './styles';
import { getProjectUrl } from './taskRouteHelper';
import type { Language, LanguageStatus, Task } from './taskTypes';

const getStatusText = (status: LanguageStatus, progressValue: number) => {
    switch (status) {
        case 'completed':
            return progressValue < 100 ? 'Closed' : 'Completed';
        case 'created':
            // For some reason project task lang status is not correctly updated to "in progress". So instead
            // we need to rely on `created` status and progress value is not 0.
            return progressValue > 0 ? 'In progress' : 'Not started';
        case 'in_progress':
            return 'In progress';
        case 'queued':
            return 'Queued';
        default:
            return '-';
    }
};

type LanguageProps = {
    language: Language;
    task: Task;
};

const TaskLanguage = ({ language, task }: LanguageProps) => {
    const progressValue = language.progress || 0;
    const [isMarkAsDonePromptShown, setIsMarkAsDonePromptShown] = useState(false);

    const isLanguageProgressCompleted =
        [task.status, language.status].every((status) => ['in_progress', 'created'].includes(status)) &&
        progressValue >= 100;

    const isLanguageCompleted = language.status === 'completed';

    const [closeLanguage, { isLoading }] = useCloseLanguageMutation();

    const handleConfirmMarkAsDone = useCallback(() => {
        track(AnalyticsEventName.TASK_WIDGET_MARK_AS_DONE_CLICKED, { task_name: task.title });

        const taskId = task.id;
        const languageId = language.lang_id;

        closeLanguage({ taskId, languageId })
            .unwrap()
            .then((data) => {
                if (data.warnings?.includes('lang_done_already')) {
                    showToast({ type: 'warning', title: `${language.name} already marked as done` });

                    setIsMarkAsDonePromptShown(false);

                    return;
                }

                if (data.finished) {
                    // @ts-expect-error Need to import this instead probably. Will be addressed later
                    window.globalSocket?.emit('task lang complete', {
                        task_id: taskId,
                        lang_id: languageId,
                        projectId: task.master_project_id,
                    });

                    document.dispatchEvent(new CustomEvent('task_lang_complete', { detail: { taskId, languageId } }));

                    if (!data.closed_task) {
                        // We're only showing this toast if the whole task isn't closed
                        showToast({ type: 'success', title: `${language.name} marked as done` });
                    }
                }

                if (data.closed_task) {
                    // @ts-expect-error Need to import this instead probably. Will be addressed later
                    window.globalSocket?.emit('task closed', {
                        task_id: data.closed_task.task_id,
                        projectId: task.master_project_id,
                    });
                    document.dispatchEvent(
                        new CustomEvent('task_closed', {
                            detail: {
                                taskId: data.closed_task.task_id,
                            },
                        }),
                    );

                    showToast({ type: 'success', title: `${task.title} completed` });
                }
                setIsMarkAsDonePromptShown(false);
            });
    }, [task, language, closeLanguage]);

    return (
        <Row $completed={isLanguageProgressCompleted}>
            <Cell>
                <Tooltip tooltip="Open in bilingual view">
                    <Link
                        href={getProjectUrl(task, 'single', language)}
                        onClick={() => {
                            track(AnalyticsEventName.TASK_WIDGET_LANGUAGE_CLICKED, { task_name: task.title });
                        }}
                    >
                        {language.name}
                    </Link>
                </Tooltip>
            </Cell>
            <Cell align="right" $secondary>
                {isLanguageProgressCompleted || isLanguageCompleted ? (
                    '0'
                ) : (
                    <Tooltip tooltip="Open uncompleted items">
                        <Link
                            href={getProjectUrl(task, 'single', language, true)}
                            onClick={() => {
                                track(AnalyticsEventName.TASK_WIDGET_WORDCOUNT_CLICKED, { task_name: task.title });
                            }}
                        >
                            {parseInt(language.base_words_total, 10) - parseInt(language.base_words_done, 10)}
                        </Link>
                    </Tooltip>
                )}
            </Cell>
            <Cell align="right">{progressValue}%</Cell>
            <Cell>
                {isMarkAsDonePromptShown && (
                    <ConfirmModal
                        opened={isMarkAsDonePromptShown}
                        loading={isLoading}
                        title={`Are you finished with ${language.name}?`}
                        confirmText="Mark as done"
                        closeText="Cancel"
                        onClose={() => {
                            setIsMarkAsDonePromptShown(false);
                        }}
                        onConfirm={handleConfirmMarkAsDone}
                    >
                        <Alert status="info">
                            Make sure you are finished and <StrongInlineContent>mark it as done</StrongInlineContent> to
                            notify your team admin. This action cannot be undone, and the language might no longer be
                            available for you to edit.
                        </Alert>
                    </ConfirmModal>
                )}
                {isLanguageProgressCompleted ? (
                    <Link
                        disabled={isLoading}
                        onClick={() => {
                            setIsMarkAsDonePromptShown(true);
                        }}
                    >
                        Mark as done
                    </Link>
                ) : (
                    <Status $status={language.status}>{getStatusText(language.status, progressValue)}</Status>
                )}
            </Cell>
        </Row>
    );
};

export default TaskLanguage;
